import { Navigate, Outlet } from "react-router";

import { useUser } from "@/contexts/UserContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";

type FeatureFlagGuardProps = {
  componentFlag: string | string[];
  /**
   * Rendered when the flag is granted. Defaults to the nested `<Outlet />` so
   * the guard can stand in as the element of a layout route that has no
   * component of its own.
   */
  children?: React.ReactNode;
  /**
   * When true (the default) an ungranted flag redirects to the scope's 404.
   * Pass false only where rendering nothing is deliberately correct.
   */
  redirectToNotFound?: boolean;
};

/**
 * Route-level feature-flag guard.
 *
 * `FeatureFlagWrapper` gates a *component* and wraps it in a `<div>`; this
 * guard gates a *route subtree* and adds no markup, so a section-level flag can
 * be enforced on a layout route without changing the rendered DOM when the flag
 * is granted.
 *
 * The distinction matters because a flag that only hides a nav item still
 * leaves every URL beneath it reachable by direct navigation, which renders a
 * screen with no data behind it.
 */
export default function FeatureFlagGuard({
  componentFlag,
  children,
  redirectToNotFound = true,
}: FeatureFlagGuardProps) {
  const { userInfo } = useUser();
  const { buildUrl } = useScopeParams();
  const isFlagEnabled = useFeatureFlag(componentFlag);

  // Grants arrive with userInfo. Until it resolves we cannot tell "ungranted"
  // from "not loaded yet", and redirecting on the latter would bounce a user
  // off a screen they are entitled to.
  if (!userInfo) return null;

  if (isFlagEnabled) return <>{children ?? <Outlet />}</>;

  if (redirectToNotFound) return <Navigate to={buildUrl("404")} replace />;

  return null;
}
